import React, { useEffect, useState } from 'react';
import { Avatar, Box, Button, Grid, Skeleton, Typography } from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import hiCellLogo from 'assets/hiCellLogo.png';
import { getSpecialists } from 'store/specialists/specialists-api.js';
import { resolveImageUrl } from 'utils/apiOrigin.js';
import {
  formatPhoneLabel,
  getSpecialistWhatsappLink,
  normalizePhone,
  normalizeSpecialists,
} from 'utils/specialists.js';

const skeletonItems = [1, 2, 3];

const SpecialistsSession = () => {
  const [specialists, setSpecialists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchSpecialists = async () => {
      try {
        const response = await getSpecialists();

        if (!isMounted) return;

        const normalizedItems = normalizeSpecialists(response.data).filter((item) => item.active);
        setSpecialists(normalizedItems);
      } catch (error) {
        if (isMounted) {
          setSpecialists([]);
        }
        console.error('Erro ao carregar especialistas da home', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchSpecialists();

    return () => {
      isMounted = false;
    };
  }, []);

  if (!loading && !specialists.length) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column-reverse', md: 'row' },
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 4,
        px: 3,
        py: 4,
        bgcolor: 'black',
        color: 'white',
        borderRadius: 2,
        mt: 6,
        boxShadow: '0 0 16px rgba(146, 178, 31, 0.3)',
      }}
    >
      <Box flex={1}>
        <Typography
          variant="h6"
          sx={{
            fontSize: { xs: '1.2rem', md: '1.5rem' },
            textAlign: 'center',
            fontWeight: 'bold',
            color: '#92b21f',
            textTransform: 'uppercase',
            mb: 2,
            textShadow: '0 0 6px rgba(146, 178, 31, 0.6)',
          }}
        >
          Fale com um de nossos especialistas!
        </Typography>

        <Grid container spacing={2} justifyContent="space-around">
          {(loading ? skeletonItems : specialists).map((spec) => {
            const imageSrc = !loading ? resolveImageUrl(spec.imageLink) || hiCellLogo : null;
            const whatsappDisabled = !loading && !normalizePhone(spec.phone);

            return (
              <Grid item xs={12} sm={6} md={3} key={loading ? spec : spec.id}>
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.03)',
                    borderRadius: 2,
                    p: 2,
                    boxShadow: '0 0 8px rgba(0, 0, 0, 0.2)',
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 0 16px rgba(146, 178, 31, 0.5)',
                    },
                  }}
                >
                  {loading ? (
                    <Skeleton
                      variant="circular"
                      width={200}
                      height={200}
                      sx={{ bgcolor: 'grey.800' }}
                    />
                  ) : (
                    <Avatar
                      src={imageSrc}
                      alt={spec.name}
                      sx={{
                        width: 200,
                        height: 200,
                        mb: 2,
                        border: '2px solid #92b21f',
                        bgcolor: 'common.white',
                      }}
                    >
                      {spec.name?.[0] || 'S'}
                    </Avatar>
                  )}

                  {loading ? (
                    <>
                      <Skeleton width="70%" sx={{ bgcolor: 'grey.800', mb: 1 }} />
                      <Skeleton width="50%" sx={{ bgcolor: 'grey.800', mb: 3 }} />
                      <Skeleton
                        variant="rounded"
                        width="100%"
                        height={44}
                        sx={{ bgcolor: 'grey.800', borderRadius: 2 }}
                      />
                    </>
                  ) : (
                    <>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        {spec.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: 'rgba(255, 255, 255, 0.72)', minHeight: 21, mb: 2 }}
                      >
                        {formatPhoneLabel(spec.phone)}
                      </Typography>
                      <Button
                        component="a"
                        href={getSpecialistWhatsappLink(spec)}
                        target="_blank"
                        rel="noreferrer"
                        disabled={whatsappDisabled}
                        variant="contained"
                        startIcon={<WhatsAppIcon />}
                        sx={{
                          mt: 'auto',
                          width: '100%',
                          backgroundColor: '#92b21f',
                          color: 'white',
                          fontWeight: 'bold',
                          px: 4,
                          py: 1.5,
                          '&:hover': {
                            backgroundColor: '#7ca317',
                          },
                        }}
                      >
                        Chamar agora
                      </Button>
                    </>
                  )}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Box>
  );
};

export default SpecialistsSession;
